/**
 * Platform tier: who runs Kortex itself, what a tenant is subscribed to, and
 * when that subscription stops granting access.
 */
export const PLATFORM_ROLES = ['NONE', 'SUPPORT', 'SUPERADMIN'];
export const isPlatformAdmin = (role) => role === 'SUPERADMIN' || role === 'SUPPORT';
export const isSuperAdmin = (role) => role === 'SUPERADMIN';
export const SUBSCRIPTION_STATUSES = [
    'TRIALING',
    'ACTIVE',
    'PAST_DUE',
    'SUSPENDED',
    'CANCELLED',
    'EXPIRED',
    /** Checkout opened, money not yet confirmed. */
    'PENDING_PAYMENT',
    /** The first charge failed, so nothing was ever paid for. */
    'PAYMENT_FAILED',
];
/**
 * Statuses that still let a tenant use the application.
 *
 * PAST_DUE is in deliberately: a renewal that failed on a subscription already
 * paid for once keeps access while the gateway retries, which is the grace
 * period a failed renewal is supposed to get.
 *
 * PENDING_PAYMENT and PAYMENT_FAILED are deliberately out: in both, nobody has
 * paid for the period being asked about. Neither can arise for a tenant already
 * inside the product - they belong to a checkout that has not completed.
 */
export const ENTITLED_STATUSES = ['TRIALING', 'ACTIVE', 'PAST_DUE'];
export const SUBSCRIPTION_STATUS_LABELS = {
    TRIALING: 'On trial',
    ACTIVE: 'Active',
    PAST_DUE: 'Payment overdue',
    SUSPENDED: 'Suspended',
    CANCELLED: 'Cancelled',
    EXPIRED: 'Expired',
    PENDING_PAYMENT: 'Awaiting payment',
    PAYMENT_FAILED: 'Payment failed',
};
export const DEFAULT_PLAN_CODE = 'FREE';
const DAY_MS = 24 * 60 * 60 * 1000;
/**
 * Days a hotel keeps working after a payment was due and did not arrive.
 *
 * Applies where money was expected: a paid period that ended, a renewal the
 * gateway could not collect, a first charge that failed when a trial ran out.
 * It does **not** apply to a trial that simply ended with nobody having tried
 * to pay - that would quietly make every "14-day trial" a 21-day one.
 *
 * Bounded on purpose. Grace that never runs out is not grace, it is free
 * software, and before this a period that ended without auto-renew stayed
 * PAST_DUE - and therefore entitled - forever.
 */
export const GRACE_DAYS = 7;
/** Where the grace window closes for an entitlement that ended at `end`. */
const graceUntil = (end) => new Date(end.getTime() + GRACE_DAYS * DAY_MS);
const inGrace = (end, now) => Boolean(end && end.getTime() <= now.getTime() && graceUntil(end).getTime() > now.getTime());
/**
 * Whole days left before `until`, never negative.
 *
 * Counted between calendar days rather than between instants. Rounding the raw
 * hours up left the number stuck: a trial started at 11pm read "30 days" that
 * night and still read 30 the whole of the next day, which is exactly what
 * somebody watching their trial notices and does not believe. Counting dates
 * means it drops by one every midnight, like a calendar does.
 *
 * The day it ends reads zero: they still have that day, and the banner says
 * so in words rather than pretending there is another one after it.
 */
export function daysRemaining(until, now = new Date()) {
    if (!until)
        return 0;
    const end = typeof until === 'string' ? new Date(until) : until;
    if (Number.isNaN(end.getTime()))
        return 0;
    const midnight = (date) => Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
    return Math.max(0, Math.round((midnight(end) - midnight(now)) / DAY_MS));
}
/**
 * The effective status, applying the passage of time.
 *
 * A trial whose end date has passed is EXPIRED whether or not anything has
 * written that to the row yet, so a read and an enforcement check can never
 * disagree.
 */
export function effectiveStatus(subscription, now = new Date()) {
    if (!subscription)
        return 'EXPIRED';
    const { status, trialEndsAt, currentPeriodEnd } = subscription;
    if (status === 'TRIALING') {
        // No grace: a trial nobody tried to pay for ends on the day it says.
        const end = trialEndsAt ? new Date(trialEndsAt) : null;
        return end && end.getTime() <= now.getTime() ? 'EXPIRED' : 'TRIALING';
    }
    /*
     * A paid period that has run out buys GRACE_DAYS more, then ends.
     *
     * The same window serves both reasons it can run out: a renewal the gateway
     * is still retrying, and a subscription whose owner turned auto-renew off.
     * Telling them apart needs `autoRenew`, and it turns out not to matter -
     * either way the hotel keeps working for a week and then does not. What was
     * wrong before was not the PAST_DUE, it was that nothing ever ended it.
     */
    if (status === 'ACTIVE' && currentPeriodEnd) {
        const end = new Date(currentPeriodEnd);
        if (end.getTime() > now.getTime())
            return 'ACTIVE';
        return inGrace(end, now) ? 'PAST_DUE' : 'EXPIRED';
    }
    if (status === 'PAST_DUE' && currentPeriodEnd) {
        const end = new Date(currentPeriodEnd);
        /*
         * Still inside the period they paid for.
         *
         * PAST_DUE says a charge failed, not that time ran out, and the two are
         * independent: a renewal can fail in October for a period already paid up
         * to November. recordFailedPayment() sets this status with the comment
         * "the current period stands" - and this read used to throw that away,
         * because `inGrace` only answers for an end that has already passed, so a
         * future one fell through to EXPIRED.
         *
         * A hotel that had paid for the month was locked out of writing the day
         * any payment failed against it, and told its plan had "expired" on a
         * date a month in the future. Money taken, access withdrawn.
         */
        if (end.getTime() > now.getTime())
            return 'PAST_DUE';
        return inGrace(end, now) ? 'PAST_DUE' : 'EXPIRED';
    }
    /*
     * A first charge that failed when the trial ended. Money was expected, so
     * the week applies - counted from the day access was due to stop, not from
     * the failure, so a gateway retrying for days cannot extend it.
     *
     * A hotel that never had a trial or a period has no date to count from and
     * no access to continue: failing to pay does not earn a free week.
     */
    if (status === 'PAYMENT_FAILED') {
        /*
         * The later of the two, not the trial by preference.
         *
         * Ordinarily this status means a first charge failed when a trial ran
         * out, so the trial date is the one that matters. But a hotel can hold a
         * paid period as well - time granted from the console, or an earlier
         * purchase - and reading the trial date in front of it would end an
         * entitlement that is still running. Whichever runs out last is the
         * honest answer to "when does access stop".
         */
        const dates = [trialEndsAt, currentPeriodEnd]
            .filter(Boolean)
            .map((value) => new Date(value))
            .filter((date) => !Number.isNaN(date.getTime()));
        if (dates.length === 0)
            return 'EXPIRED';
        const due = new Date(Math.max(...dates.map((date) => date.getTime())));
        // Paid for, and not yet used up - the failure is about the next charge.
        if (due.getTime() > now.getTime())
            return 'PAST_DUE';
        return inGrace(due, now) ? 'PAST_DUE' : 'EXPIRED';
    }
    return status;
}
/**
 * When the grace window closes, or null when there is not one open.
 *
 * For a screen that has to tell somebody how long they have left to pay.
 */
export function graceEndsAt(subscription, now = new Date()) {
    if (!subscription || effectiveStatus(subscription, now) !== 'PAST_DUE')
        return null;
    const due = subscription.currentPeriodEnd ?? subscription.trialEndsAt;
    if (!due)
        return null;
    /*
     * PAST_DUE now also covers a charge that failed while the paid period is
     * still running, where no grace window has opened yet. Returning one would
     * have a screen counting down days of grace to somebody who has a month in
     * hand - and counting from the wrong date at that.
     */
    const end = new Date(due);
    if (end.getTime() > now.getTime())
        return null;
    return graceUntil(end);
}
export const isEntitled = (subscription, now = new Date()) => ENTITLED_STATUSES.includes(effectiveStatus(subscription, now));
/** When the subscription stops granting access, for display. */
export const entitlementEndsAt = (subscription) => {
    if (!subscription)
        return null;
    const value = subscription.status === 'TRIALING' ? subscription.trialEndsAt : subscription.currentPeriodEnd;
    return value ? new Date(value) : null;
};
/**
 * Splits one tax figure the way an Indian tax invoice must show it.
 *
 * The same money, named differently depending on where it was supplied: a
 * supply inside one state is CGST and SGST at half the rate each, and one
 * that crosses a state border is IGST at the whole rate. Charging the right
 * total is not enough - an invoice that shows a single "GST 18%" line is not
 * a tax invoice, and the recipient cannot claim credit from it.
 *
 * The place of supply is the recipient's state. Where it is not known - a
 * hotel that never gave one - the supply is treated as intra-state, because
 * the seller's own state is the only one in evidence and inventing an
 * inter-state supply would move money to the wrong government.
 *
 * Halving is done in whole paise with the remainder going to CGST, so the two
 * halves always add back to the tax that was actually charged. An odd number
 * of paise split evenly is a rupee that does not reconcile.
 */
export function splitGst(taxAmount, sellerStateCode, buyerStateCode) {
    const seller = (sellerStateCode ?? '').trim();
    const buyer = (buyerStateCode ?? '').trim();
    const placeOfSupply = buyer || seller || null;
    if (taxAmount <= 0)
        return { placeOfSupply, cgst: 0, sgst: 0, igst: 0 };
    // Without both states there is no border to have crossed.
    const interState = Boolean(seller && buyer) && seller !== buyer;
    if (interState)
        return { placeOfSupply, cgst: 0, sgst: 0, igst: taxAmount };
    const paise = Math.round(taxAmount * 100);
    const half = Math.floor(paise / 2);
    return {
        placeOfSupply,
        cgst: (paise - half) / 100,
        sgst: half / 100,
        igst: 0,
    };
}
//# sourceMappingURL=platform.js.map